import React, { useState, useCallback, useRef, useEffect } from "react";
import { FiThumbsUp, FiThumbsDown, FiCopy, FiInfo } from "react-icons/fi";
import Tooltip from "../Tooltip";
import { AI_ASSISTANT_PENDO_EVENTS, trackAssistantEvent } from "../../utils/assistantAnalytics";
import "./MessageActions.scss";

/**
 * The row of actions under an assistant reply: rate it, copy it, ask what it
 * knew.
 *
 * The rating is not held here. It comes in as `rating` and goes out through
 * `onRate`, because it belongs to the turn and the turn belongs to the
 * server — a rating kept in this component would disappear on reload and
 * disagree with the same conversation open on another device.
 */
const MessageActions = ({
  rating = null,
  onRate,
  copyText = "",
  copyLink,
  onCopy,
  onShowContext,
  className = "",
}) => {
  const handleThumbsUp = useCallback(() => {
    // Pressing the same thumb again clears it. A rating the user cannot take
    // back is a rating they stop giving honestly.
    const next = rating === "up" ? null : "up";
    if (next) {
      trackAssistantEvent(AI_ASSISTANT_PENDO_EVENTS.FEEDBACK_SUBMITTED, {
        feedback: "like",
      });
    }
    onRate?.(next);
  }, [rating, onRate]);

  const handleThumbsDown = useCallback(() => {
    const next = rating === "down" ? null : "down";
    if (next) {
      trackAssistantEvent(AI_ASSISTANT_PENDO_EVENTS.FEEDBACK_SUBMITTED, {
        feedback: "dislike",
      });
    }
    onRate?.(next);
  }, [rating, onRate]);

  const [showCopied, setShowCopied] = useState(false);
  const copyFeedbackTimerRef = useRef(null);

  useEffect(() => () => {
    if (copyFeedbackTimerRef.current) clearTimeout(copyFeedbackTimerRef.current);
  }, []);

  const handleCopy = useCallback(async () => {
    const toCopy = copyLink ?? copyText;
    if (!toCopy || !onCopy) return;
    if (copyFeedbackTimerRef.current) {
      clearTimeout(copyFeedbackTimerRef.current);
      copyFeedbackTimerRef.current = null;
    }
    const result = onCopy(toCopy);
    const ok = typeof result?.then === "function" ? await result : result;
    if (ok) {
      trackAssistantEvent(
        copyLink
          ? AI_ASSISTANT_PENDO_EVENTS.LINK_COPIED
          : AI_ASSISTANT_PENDO_EVENTS.COPY_CLICKED,
        {
          copyType: copyLink ? "link" : "message",
        },
      );
      setShowCopied(true);
      copyFeedbackTimerRef.current = setTimeout(() => setShowCopied(false), 2000);
    }
  }, [copyLink, copyText, onCopy]);

  const canCopy = Boolean(copyLink || copyText);
  const copyLabel = showCopied ? "Copied" : (copyLink ? "Copy link" : "Copy message");

  return (
    <div className={`message-actions ${className}`.trim()} role="group" aria-label="Message actions">
      <Tooltip content="Mark as helpful" placement="top" variant="light">
        <button
          type="button"
          className={`message-actions__btn message-actions__btn--thumbs ${rating === "up" ? "message-actions__btn--active" : ""}`}
          onClick={handleThumbsUp}
          aria-label="Mark as helpful"
          aria-pressed={rating === "up"}
        >
          <FiThumbsUp aria-hidden />
        </button>
      </Tooltip>
      <Tooltip content="Mark as not helpful" placement="top" variant="light">
        <button
          type="button"
          className={`message-actions__btn message-actions__btn--thumbs ${rating === "down" ? "message-actions__btn--active" : ""}`}
          onClick={handleThumbsDown}
          aria-label="Mark as not helpful"
          aria-pressed={rating === "down"}
        >
          <FiThumbsDown aria-hidden />
        </button>
      </Tooltip>
      {canCopy && (
        <Tooltip content={copyLabel} placement="top" variant="light">
          <button
            type="button"
            className="message-actions__btn message-actions__btn--copy"
            onClick={handleCopy}
            aria-label={copyLabel}
          >
            <FiCopy aria-hidden />
          </button>
        </Tooltip>
      )}
      {onShowContext && (
        <Tooltip content="What Lou knew" placement="top" variant="light">
          <button
            type="button"
            className="message-actions__btn message-actions__btn--context"
            onClick={onShowContext}
            aria-label="What Lou knew"
          >
            <FiInfo aria-hidden />
          </button>
        </Tooltip>
      )}
    </div>
  );
};

export default MessageActions;
